import React from "react";
import { Reveal } from "../components/Reveal";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { AlertTriangle } from "lucide-react";

/**
 * /privacy — Privacy Policy (Draft).
 * GDPR + CCPA aware. Marked as draft pending legal review. noindex'd.
 */
export const Privacy = () => {
    const updated = "February 11, 2026";

    return (
        <>
            <SEO
                title="Privacy Policy"
                description="Privacy Policy for Cybersecurity Forefront LLC — what we collect, how we use it, sub-processors, and your GDPR / CCPA rights."
                canonicalPath="/privacy"
                noindex
            />
            <div
                className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 md:px-10"
                data-testid="privacy-page"
            >
                <Reveal>
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Legal
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05]"
                        data-testid="privacy-title"
                    >
                        Privacy Policy
                    </h1>
                    <p className="mt-3 text-[12px] font-mono uppercase tracking-[0.2em] text-text-dim">
                        Last updated: {updated}
                    </p>
                </Reveal>

                <div
                    role="note"
                    className="mt-8 flex items-start gap-3 rounded-lg border border-accent-amber/40 bg-accent-amber/[0.06] p-4"
                    data-testid="privacy-draft-banner"
                >
                    <AlertTriangle className="w-4 h-4 text-accent-amber mt-0.5 shrink-0" />
                    <p className="text-[13px] text-text-muted leading-relaxed">
                        <span className="text-accent-amber font-medium">
                            Draft — subject to legal review.
                        </span>{" "}
                        This policy is provided in good faith and reflects our current
                        practices. It will be finalized by counsel before general
                        availability. We will notify registered users of material changes.
                    </p>
                </div>

                <article className="mt-12 space-y-12 text-[14px] text-text-muted leading-[1.75]">
                    <section data-testid="privacy-section-who">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            1. Who We Are
                        </h2>
                        <p>
                            <strong className="text-text-primary">
                                Cybersecurity Forefront LLC
                            </strong>
                            , a Wyoming limited liability company (registered office: 30 N
                            Gould St, Ste N, Sheridan, WY 82801, USA), is the data
                            controller for personal data processed through{" "}
                            {SITE_DOMAIN.replace("https://", "")} and related services.
                        </p>
                    </section>

                    <section data-testid="privacy-section-collect">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            2. What We Collect
                        </h2>
                        <p>We collect the following categories of personal data:</p>
                        <ul className="mt-3 list-disc pl-6 space-y-1.5">
                            <li>
                                <strong className="text-text-primary">Account data:</strong>{" "}
                                name, email, hashed password (or Google OAuth identifier),
                                role, plan, subscription status, account timestamps.
                            </li>
                            <li>
                                <strong className="text-text-primary">Usage data:</strong>{" "}
                                pages visited, features used, IP address, user agent,
                                referrer (collected via standard server logs).
                            </li>
                            <li>
                                <strong className="text-text-primary">
                                    Customer content:
                                </strong>{" "}
                                clients, hardware inventories, subnets, configurations,
                                assessments, and chat transcripts you create inside the
                                product.
                            </li>
                            <li>
                                <strong className="text-text-primary">Billing data:</strong>{" "}
                                handled by Stripe; we receive transaction metadata but never
                                full payment-card numbers.
                            </li>
                        </ul>
                    </section>

                    <section data-testid="privacy-section-why">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            3. Why We Process Your Data
                        </h2>
                        <p>We process personal data to:</p>
                        <ul className="mt-3 list-disc pl-6 space-y-1.5">
                            <li>operate, secure, and improve the Service;</li>
                            <li>authenticate you and protect against fraud and abuse;</li>
                            <li>provide AI-assisted answers via the concierge chat;</li>
                            <li>
                                process subscriptions and send transactional emails (receipts,
                                security notices, product updates relevant to your plan);
                            </li>
                            <li>comply with legal and regulatory obligations.</li>
                        </ul>
                        <p className="mt-4">
                            Legal bases (GDPR Art. 6): contract, legitimate interest, legal
                            obligation, and — where applicable — your explicit consent.
                        </p>
                    </section>

                    <section data-testid="privacy-section-subprocessors">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            4. Sub-Processors
                        </h2>
                        <p>
                            We use the following sub-processors. Each is bound by a data
                            processing agreement and processes only the minimum data
                            necessary:
                        </p>
                        <ul className="mt-3 list-disc pl-6 space-y-1.5">
                            <li>
                                <strong className="text-text-primary">MongoDB Atlas</strong>{" "}
                                — primary database hosting (US region).
                            </li>
                            <li>
                                <strong className="text-text-primary">Stripe, Inc.</strong>{" "}
                                — payment processing.
                            </li>
                            <li>
                                <strong className="text-text-primary">
                                    Google LLC (OAuth & Workspace)
                                </strong>{" "}
                                — social login and transactional email delivery.
                            </li>
                            <li>
                                <strong className="text-text-primary">
                                    OpenAI / model providers
                                </strong>{" "}
                                — AI inference for the concierge chat. We do not send your
                                stored customer content to model providers for training.
                            </li>
                        </ul>
                    </section>

                    <section data-testid="privacy-section-retention">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            5. Data Retention
                        </h2>
                        <p>
                            We retain account data for as long as your account is active and
                            for up to 24 months after deletion for legal, accounting, and
                            anti-abuse purposes. Chat transcripts are retained for up to 12
                            months unless you delete them earlier. You can request earlier
                            deletion at any time.
                        </p>
                    </section>

                    <section data-testid="privacy-section-rights">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            6. Your Rights
                        </h2>
                        <p>
                            Under GDPR (EU/EEA/UK) and CCPA (California), you may have the
                            right to:
                        </p>
                        <ul className="mt-3 list-disc pl-6 space-y-1.5">
                            <li>access the personal data we hold about you;</li>
                            <li>correct inaccurate or incomplete data;</li>
                            <li>request deletion (&ldquo;right to be forgotten&rdquo;);</li>
                            <li>restrict or object to processing;</li>
                            <li>data portability (machine-readable export);</li>
                            <li>
                                opt out of the &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of
                                personal information (we do not sell data);
                            </li>
                            <li>
                                lodge a complaint with your local data protection authority.
                            </li>
                        </ul>
                        <p className="mt-4">
                            To exercise any right, email{" "}
                            <a
                                href="mailto:info@cybersecurityforefront.com"
                                className="text-accent-cyan hover:underline"
                            >
                                info@cybersecurityforefront.com
                            </a>
                            . We respond within 30 days (45 for CCPA, with extension where
                            permitted).
                        </p>
                    </section>

                    <section data-testid="privacy-section-cookies">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            7. Cookies & Tracking
                        </h2>
                        <p>
                            We use strictly necessary cookies for authentication and CSRF
                            protection. We do not deploy advertising cookies. Product
                            analytics, when enabled, are gated behind a runtime flag and run
                            in anonymous mode by default.
                        </p>
                    </section>

                    <section data-testid="privacy-section-international">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            8. International Transfers
                        </h2>
                        <p>
                            Personal data may be transferred to and processed in the United
                            States. For data originating in the EU/EEA/UK, transfers rely on
                            the European Commission&apos;s Standard Contractual Clauses
                            (SCCs) and supplementary measures where appropriate.
                        </p>
                    </section>

                    <section data-testid="privacy-section-security">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            9. Security
                        </h2>
                        <p>
                            We protect personal data with industry-standard measures:
                            encryption in transit (TLS 1.2+), bcrypt-hashed passwords,
                            scoped service credentials, and least-privilege access controls.
                            No system is perfectly secure; we will notify affected users
                            without undue delay in the event of a confirmed breach involving
                            personal data.
                        </p>
                    </section>

                    <section data-testid="privacy-section-children">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            10. Children
                        </h2>
                        <p>
                            The Service is not directed to children under 16, and we do not
                            knowingly collect personal data from them. If you believe a
                            child has provided us personal data, email us and we will delete
                            it.
                        </p>
                    </section>

                    <section data-testid="privacy-section-changes">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            11. Changes to This Policy
                        </h2>
                        <p>
                            We may update this policy from time to time. Material changes
                            will be notified in-product or by email at least 14 days before
                            they take effect.
                        </p>
                    </section>

                    <section data-testid="privacy-section-contact">
                        <h2 className="text-xl font-semibold text-text-primary mb-3">
                            12. Contact
                        </h2>
                        <p>
                            Privacy questions? Email{" "}
                            <a
                                href="mailto:info@cybersecurityforefront.com"
                                className="text-accent-cyan hover:underline"
                            >
                                info@cybersecurityforefront.com
                            </a>{" "}
                            or write to Cybersecurity Forefront LLC, 30 N Gould St, Ste N,
                            Sheridan, WY 82801, USA.
                        </p>
                    </section>
                </article>

                <p className="mt-16 text-[11px] font-mono uppercase tracking-[0.2em] text-text-dim text-center">
                    {SITE_DOMAIN.replace("https://", "")} · Wyoming LLC · USA
                </p>
            </div>
        </>
    );
};

export default Privacy;
